import { ChevronIcon } from "@theme/assets";
import { AnimatePresence, m } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import ReactFocusLock from "react-focus-lock";

import { Box, Divider, Flex, MsButton, Text } from "@theme/components";

import type { FC } from "react";

interface NavMobileSecondaryScreenProps {
  closeAccordion: () => void;
  children: React.ReactNode;
  title: string;
  backCTAText: string;
}

export const NavMobileSecondaryScreen: FC<NavMobileSecondaryScreenProps> = ({
  closeAccordion,
  children,
  title,
  backCTAText,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const backButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // There is a bug on mobile devices, where focusing will cause the rest of the elements
    // not to render properly with AnimatePresence when we try to focus.
    // Using onAnimationComplete to focus on the back button after elements properly render.
    if (backButtonRef.current && isMenuOpen) {
      backButtonRef.current.focus();
    }
  }, [isMenuOpen]);

  return (
    <AnimatePresence>
      <m.div
        variants={{
          hide: { x: "100%" },
          show: { x: 0 },
        }}
        initial="hide"
        animate="show"
        exit="hide"
        transition={{ ease: [1, 0, 0, 1], duration: 0.3 }}
        sx={{
          position: "absolute",
          inset: 0,
          width: "full",
          bg: "beige50",
        }}
        onAnimationComplete={(variant) => setIsMenuOpen(variant === "show")}
      >
        <ReactFocusLock returnFocus={true} disabled={!isMenuOpen}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "s200",
              alignSelf: "stretch",
              pt: ["s400", "s600"],
              px: ["s600", "s700"],
              pb: "s1400",
            }}
          >
            <Flex
              sx={{
                flex: "1 0 0",
                pb: 0,
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "s400",
                alignSelf: "stretch",
                cursor: "pointer",
              }}
              onClick={closeAccordion}
            >
              <MsButton
                variant="link"
                ref={backButtonRef}
                aria-label="Back to main navigation menu"
                tabIndex={0}
                sx={{
                  display: "flex",
                  px: 0,
                  alignItems: "center",
                  alignSelf: "stretch",
                  fontSize: "inherit",
                }}
              >
                <ChevronIcon
                  sx={{
                    transform: "rotate(180deg)",
                    height: "14px",
                    color: "grey500",
                    mr: "s300",
                  }}
                />
                <Text variant="sansBody02Regular" sx={{ color: "grey700" }}>
                  {backCTAText}
                </Text>
              </MsButton>
            </Flex>
            <Text
              variant="sansBody03Medium"
              sx={{
                color: "black",
              }}
            >
              {title}
            </Text>
            <Divider aria-hidden={true} sx={{ my: "s400", color: "grey100" }} />
            {children}
          </Box>
        </ReactFocusLock>
      </m.div>
    </AnimatePresence>
  );
};
